import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  ChevronUp, ChevronDown, ChevronsUpDown, Filter, RefreshCw,
  Calendar, ChevronLeft, ChevronRight, MousePointerClick,
  Target, TrendingUp, Play, Image, X, Download, MessageCircle
} from 'lucide-react';
import { api } from '../../src/services/api';
import { MarketingMetaAdMetric, MarketingMetaAdCreative } from '../../types';

// ─── helpers ────────────────────────────────────────────────────
const fmt = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
const fmtShort = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v);
const fmtNum = (v: number) => v.toLocaleString('pt-BR');
const fmtPct = (v: number) => `${v.toFixed(2)}%`;
const fmtTime = (s: number) => {
  if (!s) return '—';
  if (s < 60) return `${s.toFixed(0)}s`;
  return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
};
const fmtFull = (d: string) => { if (!d) return '—'; const [y, m, day] = d.split('-'); return `${day}/${m}/${y}`; };
const today = () => new Date().toISOString().split('T')[0];
const daysAgo = (n: number) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().split('T')[0]; };
const currentMonthRange = () => {
  const now = new Date();
  const first = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
  return { first, last };
};

// ─── Calendar ──────────────────────────────────────────────────
const MONTHS_PT = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho',
                   'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
const DAYS_SHORT = ['Seg','Ter','Qua','Qui','Sex','Sáb','Dom'];

const CalendarMonth: React.FC<{
  year: number; month: number;
  tempStart: string; tempEnd: string; hoverDate: string;
  onSelect: (d: string) => void; onHover: (d: string) => void;
}> = ({ year, month, tempStart, tempEnd, hoverDate, onSelect, onHover }) => {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const offset = (new Date(year, month, 1).getDay() + 6) % 7;
  const todayStr = today();
  const rangeEnd = tempEnd || hoverDate;
  const [rangeS, rangeE] = tempStart && rangeEnd
    ? (tempStart <= rangeEnd ? [tempStart, rangeEnd] : [rangeEnd, tempStart])
    : ['', ''];
  const cells: (string | null)[] = Array(offset).fill(null);
  for (let d = 1; d <= daysInMonth; d++)
    cells.push(`${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`);

  return (
    <div className="p-3 w-60">
      <p className="text-xs font-semibold text-center text-gray-700 dark:text-gray-200 mb-2">{MONTHS_PT[month]} {year}</p>
      <div className="grid grid-cols-7 mb-1">
        {DAYS_SHORT.map(d => <div key={d} className="text-center text-[10px] font-medium text-gray-400 py-0.5">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-y-0.5">
        {cells.map((dateStr, i) => {
          if (!dateStr) return <div key={`e${i}`} className="h-7" />;
          const isStart = dateStr === tempStart, isEnd = dateStr === (tempEnd || hoverDate);
          const inRange = !!(rangeS && rangeE && dateStr > rangeS && dateStr < rangeE);
          return (
            <button key={dateStr} onMouseEnter={() => onHover(dateStr)} onClick={() => onSelect(dateStr)}
              className={[
                'h-7 w-7 text-xs font-medium flex items-center justify-center mx-auto transition-all select-none',
                isStart || isEnd ? 'rounded-full bg-primary text-white shadow-sm' :
                inRange ? 'bg-primary/15 text-primary dark:text-green-300 rounded-none' :
                'rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300',
                dateStr === todayStr && !isStart && !isEnd ? 'ring-1 ring-primary ring-offset-1' : ''
              ].join(' ')}>
              {parseInt(dateStr.split('-')[2])}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const DateRangePicker: React.FC<{ startDate: string; endDate: string; onChange: (s: string, e: string) => void }> = ({ startDate, endDate, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempStart, setTempStart] = useState(startDate);
  const [tempEnd, setTempEnd] = useState(endDate);
  const [hoverDate, setHoverDate] = useState('');
  const [phase, setPhase] = useState<'start' | 'end'>('start');
  const [viewYear, setViewYear] = useState(() => new Date((startDate || today()) + 'T12:00:00').getFullYear());
  const [viewMonth, setViewMonth] = useState(() => new Date((startDate || today()) + 'T12:00:00').getMonth());
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fn = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false); };
    if (isOpen) document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [isOpen]);
  useEffect(() => { setTempStart(startDate); setTempEnd(endDate); }, [startDate, endDate]);

  const open = () => {
    const d = new Date((startDate || today()) + 'T12:00:00');
    setViewYear(d.getFullYear()); setViewMonth(d.getMonth());
    setTempStart(startDate); setTempEnd(endDate); setPhase('start'); setIsOpen(true);
  };
  const handleSelect = (dateStr: string) => {
    if (phase === 'start' || dateStr < tempStart) { setTempStart(dateStr); setTempEnd(''); setPhase('end'); }
    else { setTempEnd(dateStr); setPhase('start'); }
  };
  const prevMonth = () => { if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); } else setViewMonth(m => m - 1); };
  const nextMonth = () => { if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); } else setViewMonth(m => m + 1); };
  const month2 = viewMonth === 11 ? 0 : viewMonth + 1;
  const year2 = viewMonth === 11 ? viewYear + 1 : viewYear;
  const dayCount = tempStart && tempEnd
    ? Math.round((new Date(tempEnd + 'T12:00:00').getTime() - new Date(tempStart + 'T12:00:00').getTime()) / 86400000) + 1
    : null;

  return (
    <div className="relative" ref={ref}>
      <button onClick={open} className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">
        <Calendar size={13} />
        <span className="font-semibold">{fmtFull(startDate)}</span>
        <span className="text-gray-400">→</span>
        <span className="font-semibold">{fmtFull(endDate)}</span>
      </button>
      {isOpen && (
        <div className="absolute left-0 top-full mt-2 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl">
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 dark:border-gray-700">
            <button onClick={prevMonth} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"><ChevronLeft size={15} /></button>
            <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">{phase === 'start' ? 'Clique para definir o início' : 'Clique para definir o fim'}</span>
            <button onClick={nextMonth} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"><ChevronRight size={15} /></button>
          </div>
          <div className="flex divide-x divide-gray-100 dark:divide-gray-700" onMouseLeave={() => setHoverDate('')}>
            <CalendarMonth year={viewYear} month={viewMonth} tempStart={tempStart} tempEnd={tempEnd} hoverDate={hoverDate} onSelect={handleSelect} onHover={setHoverDate} />
            <CalendarMonth year={year2} month={month2} tempStart={tempStart} tempEnd={tempEnd} hoverDate={hoverDate} onSelect={handleSelect} onHover={setHoverDate} />
          </div>
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 dark:border-gray-700">
            <span className="text-xs text-gray-500">{dayCount ? `${dayCount} dias selecionados` : 'Selecione o período'}</span>
            <div className="flex gap-2">
              <button onClick={() => { setTempStart(''); setTempEnd(''); setPhase('start'); }} className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">Limpar</button>
              <button onClick={() => { if (tempStart && tempEnd) { onChange(tempStart, tempEnd); setIsOpen(false); } }} disabled={!tempStart || !tempEnd} className="px-3 py-1.5 text-xs font-semibold bg-primary text-white rounded-lg disabled:opacity-40 hover:bg-primary/90">Aplicar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Aggregated ad type ────────────────────────────────────────
interface AggregatedAd {
  ad_id: string; ad_name: string; campaign_name: string; adset_name: string;
  spend: number; impressions: number; reach: number; link_clicks: number;
  results_total: number; conversations_started: number; leads: number;
  ctr: number; cpm: number; cost_per_result: number; frequency: number;
  video_avg_time: number; video_p25: number; video_p50: number;
  video_p75: number; video_p100: number; hasVideoData: boolean;
}

type SortKey = keyof Omit<AggregatedAd, 'ad_id' | 'ad_name' | 'campaign_name' | 'adset_name' | 'hasVideoData'>;

// ─── Video completion bar ──────────────────────────────────────
const VideoBar: React.FC<{ p25: number; p50: number; p75: number; p100: number }> = ({ p25, p50, p75, p100 }) => {
  if (!p25) return <span className="text-gray-400 text-xs">—</span>;
  const max = Math.max(p25, 1);
  return (
    <div className="flex items-center gap-1 min-w-[100px]">
      {[{ label:'25%',val:p25,color:'bg-blue-300'},{ label:'50%',val:p50,color:'bg-blue-400'},
        { label:'75%',val:p75,color:'bg-blue-500'},{ label:'100%',val:p100,color:'bg-blue-700'}].map(({label,val,color}) => (
        <div key={label} title={`${label}: ${fmtNum(val)}`}
          className={`h-4 rounded-sm ${color} transition-all`}
          style={{ width: `${Math.round((val / max) * 28)}px`, minWidth: val > 0 ? '4px' : '0' }} />
      ))}
      <span className="text-[10px] text-gray-400 ml-1">{fmtNum(p100)}</span>
    </div>
  );
};

// ─── Sort header ───────────────────────────────────────────────
const SortTh: React.FC<{
  label: string; sortK: SortKey; current: SortKey; dir: 'asc' | 'desc';
  onSort: (k: SortKey) => void; className?: string;
}> = ({ label, sortK, current, dir, onSort, className = '' }) => (
  <th onClick={() => onSort(sortK)}
    className={`px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 select-none whitespace-nowrap ${className}`}>
    <div className="flex items-center gap-1">
      {label}
      {current === sortK
        ? (dir === 'desc' ? <ChevronDown size={12} className="text-primary" /> : <ChevronUp size={12} className="text-primary" />)
        : <ChevronsUpDown size={12} className="opacity-30" />}
    </div>
  </th>
);

// ─── Ad Preview Modal ──────────────────────────────────────────
const AdPreviewModal: React.FC<{
  ad: AggregatedAd;
  creative?: MarketingMetaAdCreative;
  onClose: () => void;
}> = ({ ad, creative, onClose }) => {
  const thumbUrl = creative?.thumbnail_url || creative?.image_url || '';
  const isVideo = !!(creative?.video_id);
  const downloadImageUrl = creative?.image_url || creative?.thumbnail_url || '';
  const [bodyExpanded, setBodyExpanded] = useState(false);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col md:flex-row overflow-hidden max-h-[92vh]"
        onClick={e => e.stopPropagation()}
      >
        {/* ── Coluna esquerda: preview do anúncio ── */}
        <div className="md:w-72 flex-shrink-0 bg-gray-100 dark:bg-gray-800 flex flex-col items-center justify-start p-4 overflow-y-auto">
          <div className="w-full max-w-[260px] bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-md">

            {/* Header */}
            <div className="flex items-center gap-2 p-3">
              <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center flex-shrink-0 text-white text-xs font-bold">VE</div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[13px] text-gray-900 dark:text-white leading-tight">Vigor Energy</p>
                <p className="text-[11px] text-gray-500">Anúncio · 🌐</p>
              </div>
              <span className="text-gray-400 text-lg select-none">···</span>
            </div>

            {/* Body text */}
            {creative?.body && (
              <div className="px-3 pb-2">
                <p className={`text-[12px] text-gray-700 dark:text-gray-300 ${bodyExpanded ? 'whitespace-pre-wrap' : 'line-clamp-3'}`}>
                  {creative.body}
                </p>
                {!bodyExpanded && (
                  <button onClick={() => setBodyExpanded(true)} className="text-[11px] text-gray-400 hover:text-gray-600">...Ver mais</button>
                )}
              </div>
            )}

            {/* Media */}
            <div className="relative bg-gray-100 dark:bg-gray-800" style={{ aspectRatio: '4/5' }}>
              {thumbUrl ? (
                <img src={thumbUrl} alt={ad.ad_name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Image size={28} className="text-gray-300" />
                </div>
              )}
              {isVideo && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-black/40 rounded-full p-3">
                    <Play size={22} className="text-white" fill="white" />
                  </div>
                </div>
              )}
              {isVideo && (
                <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-2 py-1 flex items-center justify-between text-white text-[10px]">
                  <Play size={9} fill="white" />
                  <span>0:00 / 0:27</span>
                  <span>🔇</span>
                </div>
              )}
            </div>

            {/* CTA */}
            {(creative?.title || creative?.call_to_action) && (
              <div className="p-3 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                {creative.call_to_action && (
                  <p className="text-[9px] text-gray-400 uppercase font-bold tracking-wide mb-0.5">
                    {creative.call_to_action.replace(/_/g, ' ')}
                  </p>
                )}
                {creative.title && (
                  <p className="font-semibold text-[13px] text-gray-900 dark:text-white mb-2 leading-snug">{creative.title}</p>
                )}
                {creative.call_to_action === 'WHATSAPP_MESSAGE' && (
                  <div className="flex items-center justify-center gap-2 bg-[#25D366] text-white py-1.5 px-3 rounded-lg text-[12px] font-semibold">
                    <MessageCircle size={14} />
                    WhatsApp
                  </div>
                )}
                {creative.call_to_action && creative.call_to_action !== 'WHATSAPP_MESSAGE' && (
                  <div className="flex items-center justify-center bg-blue-600 text-white py-1.5 px-3 rounded-lg text-[12px] font-semibold">
                    {creative.call_to_action.replace(/_/g, ' ')}
                  </div>
                )}
              </div>
            )}

            {/* Reactions */}
            <div className="px-3 py-2 border-t border-gray-100 dark:border-gray-700 text-[11px] text-gray-500 flex items-center gap-1">
              <span>👍</span><span>2</span>
            </div>
          </div>
        </div>

        {/* ── Coluna direita: métricas e info ── */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-start justify-between p-5 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
            <div className="min-w-0 pr-4">
              <h2 className="text-base font-bold text-gray-900 dark:text-white leading-snug">{ad.ad_name}</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{ad.campaign_name}</p>
              {ad.adset_name && <p className="text-[11px] text-gray-400 dark:text-gray-500">{ad.adset_name}</p>}
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 flex-shrink-0">
              <X size={18} />
            </button>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto p-5 space-y-5">
            {/* Performance metrics */}
            <div>
              <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2">Performance</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { label: 'Investimento', value: fmt(ad.spend) },
                  { label: 'Resultados', value: fmtNum(ad.results_total) },
                  { label: 'CTR', value: ad.ctr > 0 ? fmtPct(ad.ctr) : '—' },
                  { label: 'CPR', value: ad.cost_per_result > 0 ? fmt(ad.cost_per_result) : '—' },
                  { label: 'Impressões', value: fmtNum(ad.impressions) },
                  { label: 'Cliques', value: fmtNum(ad.link_clicks) },
                  { label: 'CPM', value: ad.cpm > 0 ? fmt(ad.cpm) : '—' },
                  { label: 'Frequência', value: ad.frequency > 0 ? ad.frequency.toFixed(2) : '—' },
                ].map(({ label, value }) => (
                  <div key={label} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-2.5">
                    <p className="text-[9px] text-gray-400 uppercase font-semibold tracking-wide mb-0.5">{label}</p>
                    <p className="text-sm font-bold text-gray-800 dark:text-white">{value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Video metrics */}
            {ad.hasVideoData && (
              <div>
                <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2">Vídeo</p>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-2.5">
                    <p className="text-[9px] text-gray-400 uppercase mb-0.5">Tempo Médio</p>
                    <p className="text-sm font-bold text-gray-800 dark:text-white">{fmtTime(ad.video_avg_time)}</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-2.5">
                    <p className="text-[9px] text-gray-400 uppercase mb-0.5">Concluíram 100%</p>
                    <p className="text-sm font-bold text-gray-800 dark:text-white">{fmtNum(ad.video_p100)}</p>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-2.5">
                  <p className="text-[9px] text-gray-400 uppercase mb-1.5">Retenção por etapa</p>
                  <VideoBar p25={ad.video_p25} p50={ad.video_p50} p75={ad.video_p75} p100={ad.video_p100} />
                </div>
              </div>
            )}

            {/* Body text full */}
            {creative?.body && (
              <div>
                <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2">Texto do anúncio</p>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 max-h-36 overflow-y-auto">
                  <p className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">{creative.body}</p>
                </div>
              </div>
            )}

            {/* Downloads */}
            <div>
              <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2">Downloads</p>
              {isVideo ? (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-xs text-amber-700 dark:text-amber-400">
                  Vídeos de anúncios do Meta são privados e não podem ser baixados diretamente por aqui.
                  Acesse o{' '}
                  <a
                    href="https://adsmanager.facebook.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline font-semibold"
                  >
                    Gerenciador de Anúncios
                  </a>
                  {' '}para baixar o criativo original.
                </div>
              ) : downloadImageUrl ? (
                <a
                  href={downloadImageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <Download size={15} />
                  Baixar imagem
                </a>
              ) : (
                <p className="text-xs text-gray-400">Nenhum arquivo disponível.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main component ────────────────────────────────────────────
const CreativosView: React.FC = () => {
  const [adMetrics, setAdMetrics]               = useState<MarketingMetaAdMetric[]>([]);
  const [adCreatives, setAdCreatives]           = useState<MarketingMetaAdCreative[]>([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState<string[]>([]);
  const [startDate, setStartDate]               = useState(daysAgo(29));
  const [endDate, setEndDate]                   = useState(today());
  const [loading, setLoading]                   = useState(true);
  const [error, setError]                       = useState<string | null>(null);
  const [campaignOpen, setCampaignOpen]         = useState(false);
  const [sortKey, setSortKey]                   = useState<SortKey>('spend');
  const [sortDir, setSortDir]                   = useState<'asc' | 'desc'>('desc');
  const [selectedAd, setSelectedAd]             = useState<AggregatedAd | null>(null);
  const [selectedCreative, setSelectedCreative] = useState<MarketingMetaAdCreative | undefined>(undefined);

  const { first: cmFirst, last: cmLast } = currentMonthRange();

  const loadData = async () => {
    setLoading(true); setError(null);
    try {
      const data = await api.marketing.getAdMetrics(startDate, endDate);
      const uniqueCampaigns = [...new Set(data.map(m => m.campaign_name))].sort();
      const uniqueAdIds = [...new Set(data.map(m => m.ad_id).filter(Boolean))];
      const creatives = await api.marketing.getAdCreatives(uniqueAdIds);
      setAdMetrics(data);
      setAdCreatives(creatives);
      setSelectedCampaigns(uniqueCampaigns);
    } catch (e: any) {
      setError(e.message || 'Erro ao carregar dados.');
    } finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, [startDate, endDate]);

  const isRangeActive = (days: number) => startDate === daysAgo(days - 1) && endDate === today();
  const isCurrentMonthActive = startDate === cmFirst && endDate === cmLast;

  const allCampaigns = useMemo(
    () => [...new Set(adMetrics.map(m => m.campaign_name))].sort(),
    [adMetrics]
  );

  const creativeMap = useMemo(() => {
    const map = new Map<string, MarketingMetaAdCreative>();
    for (const c of adCreatives) map.set(c.ad_id, c);
    return map;
  }, [adCreatives]);

  const filtered = useMemo(
    () => adMetrics.filter(m => selectedCampaigns.includes(m.campaign_name)),
    [adMetrics, selectedCampaigns]
  );

  const aggregated = useMemo((): AggregatedAd[] => {
    const map = new Map<string, any>();
    for (const m of filtered) {
      const key = (m.ad_id || m.ad_name) + '|' + m.campaign_name;
      const cur = map.get(key) ?? {
        ad_id: m.ad_id || '', ad_name: m.ad_name, campaign_name: m.campaign_name, adset_name: m.adset_name || '',
        spend: 0, impressions: 0, reach: 0, link_clicks: 0, results_total: 0,
        conversations_started: 0, leads: 0, cpm_sum: 0, cpm_count: 0, freq_sum: 0, freq_count: 0,
        video_avg_sum: 0, video_avg_count: 0, video_p25: 0, video_p50: 0, video_p75: 0, video_p95: 0, video_p100: 0,
      };
      cur.spend              += Number(m.spend) || 0;
      cur.impressions        += Number(m.impressions) || 0;
      cur.reach              += Number(m.reach) || 0;
      cur.link_clicks        += Number(m.link_clicks) || 0;
      cur.results_total      += Number(m.results_total) || 0;
      cur.conversations_started += Number(m.conversations_started) || 0;
      cur.leads              += Number(m.leads) || 0;
      if (m.cpm > 0)                    { cur.cpm_sum += Number(m.cpm); cur.cpm_count++; }
      if (m.frequency > 0)              { cur.freq_sum += Number(m.frequency); cur.freq_count++; }
      if (m.video_avg_time_watched > 0) { cur.video_avg_sum += Number(m.video_avg_time_watched); cur.video_avg_count++; }
      cur.video_p25  += Number(m.video_p25_watched) || 0;
      cur.video_p50  += Number(m.video_p50_watched) || 0;
      cur.video_p75  += Number(m.video_p75_watched) || 0;
      cur.video_p95  += Number(m.video_p95_watched) || 0;
      cur.video_p100 += Number(m.video_p100_watched) || 0;
      map.set(key, cur);
    }
    return Array.from(map.values()).map(v => ({
      ad_id: v.ad_id, ad_name: v.ad_name, campaign_name: v.campaign_name, adset_name: v.adset_name,
      spend:           parseFloat(v.spend.toFixed(2)),
      impressions:     v.impressions, reach: v.reach, link_clicks: v.link_clicks,
      results_total:   v.results_total, conversations_started: v.conversations_started, leads: v.leads,
      ctr:             v.impressions > 0 ? parseFloat(((v.link_clicks / v.impressions) * 100).toFixed(2)) : 0,
      cpm:             v.cpm_count > 0 ? parseFloat((v.cpm_sum / v.cpm_count).toFixed(2)) : 0,
      cost_per_result: v.results_total > 0 ? parseFloat((v.spend / v.results_total).toFixed(2)) : 0,
      frequency:       v.freq_count > 0 ? parseFloat((v.freq_sum / v.freq_count).toFixed(2)) : 0,
      video_avg_time:  v.video_avg_count > 0 ? parseFloat((v.video_avg_sum / v.video_avg_count).toFixed(1)) : 0,
      video_p25: v.video_p25, video_p50: v.video_p50, video_p75: v.video_p75, video_p100: v.video_p100,
      hasVideoData: v.video_avg_count > 0 || v.video_p25 > 0,
    }));
  }, [filtered]);

  const hasAnyVideoData = aggregated.some(a => a.hasVideoData);

  const sorted = useMemo(() => {
    return [...aggregated].sort((a, b) => {
      const av = a[sortKey] as number;
      const bv = b[sortKey] as number;
      return sortDir === 'desc' ? bv - av : av - bv;
    });
  }, [aggregated, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const toggleCampaign = (name: string) =>
    setSelectedCampaigns(prev => prev.includes(name) ? prev.filter(c => c !== name) : [...prev, name]);

  const openModal = (ad: AggregatedAd) => {
    setSelectedAd(ad);
    setSelectedCreative(creativeMap.get(ad.ad_id));
  };

  const topPerformers = useMemo(() => {
    if (aggregated.length < 2) return null;
    const byResults = [...aggregated].sort((a, b) => b.results_total - a.results_total)[0];
    const byCtr     = [...aggregated].filter(a => a.ctr > 0).sort((a, b) => b.ctr - a.ctr)[0];
    const byCpr     = [...aggregated].filter(a => a.cost_per_result > 0).sort((a, b) => a.cost_per_result - b.cost_per_result)[0];
    return { byResults, byCtr, byCpr };
  }, [aggregated]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-gray-500 dark:text-gray-400">Carregando criativos...</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-5">
      {/* ── Filtros ─────────────────────────────────── */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1">
            {([7, 15, 30, 90] as const).map(d => (
              <button key={d} onClick={() => { setStartDate(daysAgo(d - 1)); setEndDate(today()); }}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-all ${isRangeActive(d) ? 'bg-primary text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>{d}d</button>
            ))}
            <button onClick={() => { setStartDate(cmFirst); setEndDate(cmLast); }}
              className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-all ${isCurrentMonthActive ? 'bg-primary text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>Mês atual</button>
          </div>

          <DateRangePicker startDate={startDate} endDate={endDate} onChange={(s, e) => { setStartDate(s); setEndDate(e); }} />

          <div className="relative">
            <button onClick={() => setCampaignOpen(!campaignOpen)} className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">
              <Filter size={13} />
              Campanhas
              {selectedCampaigns.length < allCampaigns.length && <span className="bg-primary text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{selectedCampaigns.length}</span>}
            </button>
            {campaignOpen && (
              <div className="absolute left-0 top-full mt-1 z-30 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl min-w-[240px] p-2">
                <div className="flex justify-between items-center px-2 pb-2 border-b border-gray-100 dark:border-gray-700 mb-1">
                  <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">Filtrar campanhas</span>
                  <button onClick={() => setSelectedCampaigns(selectedCampaigns.length === allCampaigns.length ? [] : [...allCampaigns])} className="text-[11px] text-primary hover:underline font-medium">{selectedCampaigns.length === allCampaigns.length ? 'Desmarcar todas' : 'Marcar todas'}</button>
                </div>
                {allCampaigns.map(name => (
                  <label key={name} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                    <input type="checkbox" checked={selectedCampaigns.includes(name)} onChange={() => toggleCampaign(name)} className="rounded accent-primary" />
                    <span className="text-xs text-gray-700 dark:text-gray-200 truncate max-w-[190px]">{name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <button onClick={loadData} className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <RefreshCw size={12} />Atualizar
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 text-sm text-red-700 dark:text-red-400">{error}</div>}

      {aggregated.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
          <p className="text-gray-400 dark:text-gray-500 font-medium">Nenhum dado de criativo encontrado.</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Verifique o período ou configure o workflow n8n.</p>
        </div>
      ) : (
        <>
          {/* ── Top performers ───────────────────────── */}
          {topPerformers && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { icon: Target, label: 'Mais Resultados', color: 'bg-blue-500', ad: topPerformers.byResults, metric: `${fmtNum(topPerformers.byResults.results_total)} resultados` },
                { icon: MousePointerClick, label: 'Melhor CTR', color: 'bg-teal-500', ad: topPerformers.byCtr, metric: topPerformers.byCtr ? fmtPct(topPerformers.byCtr.ctr) : '—' },
                { icon: TrendingUp, label: 'Menor CPR', color: 'bg-amber-500', ad: topPerformers.byCpr, metric: topPerformers.byCpr ? fmt(topPerformers.byCpr.cost_per_result) : '—' },
              ].map(({ icon: Icon, label, color, ad, metric }) => ad && (
                <button key={label} onClick={() => openModal(ad)}
                  className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex items-start gap-3 text-left hover:shadow-md transition-shadow w-full">
                  <div className={`p-2 rounded-lg ${color} flex-shrink-0`}><Icon size={16} className="text-white" /></div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">{label}</p>
                    <p className="text-xs font-semibold text-gray-800 dark:text-white mt-0.5 truncate">{ad.ad_name}</p>
                    <p className="text-[10px] text-gray-400 truncate">{ad.campaign_name}</p>
                    <p className="text-sm font-bold text-gray-900 dark:text-white mt-1">{metric}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* ── Tabela ───────────────────────────────── */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-white">
                Todos os criativos
                <span className="ml-2 text-xs font-normal text-gray-400">({sorted.length})</span>
              </h3>
              <p className="text-xs text-gray-400 dark:text-gray-500">Clique em um criativo para ver o preview</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900/50">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide w-12"></th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide min-w-[200px]">Anúncio</th>
                    <SortTh label="Investimento"  sortK="spend"           current={sortKey} dir={sortDir} onSort={handleSort} />
                    <SortTh label="Impressões"     sortK="impressions"     current={sortKey} dir={sortDir} onSort={handleSort} />
                    <SortTh label="Cliques"        sortK="link_clicks"     current={sortKey} dir={sortDir} onSort={handleSort} />
                    <SortTh label="CTR"            sortK="ctr"             current={sortKey} dir={sortDir} onSort={handleSort} />
                    <SortTh label="Resultados"     sortK="results_total"   current={sortKey} dir={sortDir} onSort={handleSort} />
                    <SortTh label="CPR"            sortK="cost_per_result" current={sortKey} dir={sortDir} onSort={handleSort} />
                    <SortTh label="CPM"            sortK="cpm"             current={sortKey} dir={sortDir} onSort={handleSort} />
                    <SortTh label="Freq."          sortK="frequency"       current={sortKey} dir={sortDir} onSort={handleSort} />
                    {hasAnyVideoData && <SortTh label="Tempo Médio" sortK="video_avg_time" current={sortKey} dir={sortDir} onSort={handleSort} />}
                    {hasAnyVideoData && <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">Conclusão</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {sorted.map((ad, i) => {
                    const creative = creativeMap.get(ad.ad_id);
                    const thumbUrl = creative?.thumbnail_url || creative?.image_url || '';
                    return (
                      <tr key={i}
                        className="hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors cursor-pointer"
                        onClick={() => openModal(ad)}
                      >
                        <td className="px-3 py-2.5">
                          <div className="w-9 h-12 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex-shrink-0">
                            {thumbUrl ? (
                              <img src={thumbUrl} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Image size={12} className="text-gray-300" />
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2.5">
                          <p className="text-xs font-semibold text-gray-800 dark:text-white truncate max-w-[200px]" title={ad.ad_name}>{ad.ad_name}</p>
                          <p className="text-[10px] text-gray-400 dark:text-gray-500 truncate max-w-[200px]">{ad.campaign_name}</p>
                        </td>
                        <td className="px-3 py-2.5 text-xs font-semibold text-gray-700 dark:text-gray-200 whitespace-nowrap">{fmt(ad.spend)}</td>
                        <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">{fmtNum(ad.impressions)}</td>
                        <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">{fmtNum(ad.link_clicks)}</td>
                        <td className="px-3 py-2.5 text-xs whitespace-nowrap">
                          <span className={`font-semibold ${ad.ctr > 2 ? 'text-green-600 dark:text-green-400' : ad.ctr > 1 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-600 dark:text-gray-300'}`}>
                            {ad.ctr > 0 ? fmtPct(ad.ctr) : '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-xs font-semibold text-gray-700 dark:text-gray-200 whitespace-nowrap">{ad.results_total > 0 ? fmtNum(ad.results_total) : <span className="text-gray-300 dark:text-gray-600">—</span>}</td>
                        <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">{ad.cost_per_result > 0 ? fmt(ad.cost_per_result) : <span className="text-gray-300 dark:text-gray-600">—</span>}</td>
                        <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">{ad.cpm > 0 ? fmtShort(ad.cpm) : '—'}</td>
                        <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">{ad.frequency > 0 ? ad.frequency.toFixed(2) : '—'}</td>
                        {hasAnyVideoData && (
                          <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">
                            {ad.video_avg_time > 0 ? (
                              <span className="flex items-center gap-1"><Play size={10} className="text-blue-500" />{fmtTime(ad.video_avg_time)}</span>
                            ) : '—'}
                          </td>
                        )}
                        {hasAnyVideoData && (
                          <td className="px-3 py-2.5">
                            <VideoBar p25={ad.video_p25} p50={ad.video_p50} p75={ad.video_p75} p100={ad.video_p100} />
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ── Modal ────────────────────────────────────── */}
      {selectedAd && (
        <AdPreviewModal
          ad={selectedAd}
          creative={selectedCreative}
          onClose={() => { setSelectedAd(null); setSelectedCreative(undefined); }}
        />
      )}
    </div>
  );
};

export default CreativosView;
